import { Component, Inject, OnInit } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Cart, DeliveryInformation, GetDeliveryMomentsResponse } from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, DIALOG_RETURN_TYPES, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';

import { ProductDeliveryInformationComponent } from '../../product/components/product-delivery-information/product-delivery-information.component';
import { ProductWizardMode } from '../../product/models/product.models';
import * as ProductActions from '../../product/store/actions';
import * as ProductSelectors from '../../product/store/selectors';

export interface CartFlexibilityDateViewModel extends Cart {
  isUserAllowedToOrderItem: boolean;
  proposedDeliveryDate?: string;
  isExactWeighingShipTo: boolean;
}
export interface ChooseFlexibilityDateDialogData extends AbstractDialogData {
  cartFlexibilityDate?: CartFlexibilityDateViewModel;
  deliveryInformation?: DeliveryInformation;
}

export interface ChooseFlexibilityDateDialogResult {
  deliveryInformation: DeliveryInformation;
}

@UntilDestroy()
@Component({
  selector: 'app-choose-flexibility-date-dialog',
  templateUrl: './choose-flexibility-date-dialog.component.html',
  styleUrl: './choose-flexibility-date-dialog.component.scss',
  imports: [AgrDialogModule, ProductDeliveryInformationComponent],
})
export class ChooseFlexibilityDateDialogComponent extends AbstractDialog<ChooseFlexibilityDateDialogData> implements OnInit {
  deliveryMomentsResponse: GetDeliveryMomentsResponse;
  deliveryInformation: DeliveryInformation;
  allowedFlexibility: number;

  private readonly deliveryMoments$ = this.store.select(ProductSelectors.selectDeliveryMoments);
  private selectedDeliveryInformation: DeliveryInformation;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChooseFlexibilityDateDialogData,
    protected readonly mdDialogRef: MatDialogRef<ChooseFlexibilityDateDialogComponent, AbstractDialogResultData<ChooseFlexibilityDateDialogResult>>,
    private readonly localeService: LocaleService,
    private readonly store: Store,
  ) {
    super();
    this.deliveryInformation = data.deliveryInformation;
    this.selectedDeliveryInformation = { ...data.deliveryInformation };
    this.allowedFlexibility =
      data.cartFlexibilityDate.lines.length > 0
        ? data.cartFlexibilityDate.lines.reduce((minFlexibility, line) => {
            const lineFlexibility = line.orderingInformation.flexibility ?? 0;
            return Math.min(minFlexibility, lineFlexibility);
          }, Number.MAX_VALUE)
        : 0;
  }

  ngOnInit(): void {
    this.data.title = this.localeService.translate('delivery-date.cart-tab-title');
    this.data.confirmButton = true;
    this.data.confirmButtonText = this.localeService.translate('delivery-date.confirm');

    this.store.dispatch(
      ProductActions.getCartDeliveryDatesRequest({
        cartId: this.data.cartFlexibilityDate.id,
        flexibility: this.data.deliveryInformation.flexibility ?? 0,
      }),
    );
    this.deliveryMoments$.pipe(untilDestroyed(this)).subscribe((deliveryMomentsResponse) => {
      if (deliveryMomentsResponse) {
        this.deliveryMomentsResponse = deliveryMomentsResponse;
        const deliveryDate = deliveryMomentsResponse.deliveryDates.includes(this.selectedDeliveryInformation.deliveryDate)
          ? this.selectedDeliveryInformation.deliveryDate
          : deliveryMomentsResponse.proposedDate;
        this.deliveryInformation = {
          deliveryDate,
          flexibility: this.selectedDeliveryInformation.flexibility,
        };
      }
    });
  }

  confirmDeliveryDate() {
    this.store.dispatch(
      ProductActions.patchCartDeliveryDateAndFlexibilityRequest({
        cartId: this.data.cartFlexibilityDate.id,
        deliveryDate: this.selectedDeliveryInformation.deliveryDate,
        flexibility: this.selectedDeliveryInformation.flexibility,
      }),
    );

    this.mdDialogRef.close({ status: DIALOG_RETURN_TYPES.confirm, result: { deliveryInformation: this.selectedDeliveryInformation } });
  }

  handleDeliveryInformationChange(selectedDeliveryInformation: DeliveryInformation): void {
    this.selectedDeliveryInformation = { ...selectedDeliveryInformation, deliveryDate: selectedDeliveryInformation?.deliveryDate?.split('.')[0] + 'Z' };
  }

  handleDeliveryInformationIsValidChange(isDataValid: boolean): void {
    this.data.disableConfirmButton = !isDataValid;
  }

  protected readonly ProductWizardMode = ProductWizardMode;
}
